import DropdownButton from '@components/button/DropdownButton'
import SplitButton from '@components/button/SplitButton'
import { ActionButton } from '@components/button/types'
import { tabletQuery } from '@globals/themes/themeConstants'
import Button, { ButtonProps } from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { SxProps } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { MOBILE_BOTTOM_NAV_HEIGHT } from '@nav/Nav.constants'
import { find } from 'lodash/fp'
import { Link } from 'react-router'

const FAB_ID = 'onboarding-guide-fab'

export const ACTION_BUTTONS_OFFSET = MOBILE_BOTTOM_NAV_HEIGHT + 24

const mobileContainerStyle: SxProps = {
  position: 'fixed',
  bottom: ACTION_BUTTONS_OFFSET,
  left: 16,
  right: 16,
  display: 'flex',
  justifyContent: 'flex-start',
  zIndex: 900, // keep under the onboarding FAB (zIndex 1000)
}

type ActionButtonsProps = {
  actions: ActionButton[]
}

const ActionButtons: React.FC<ActionButtonsProps> = ({ actions }) => {
  const isTablet = useMediaQuery(tabletQuery)

  const visibleActions = actions.filter(action =>
    action.visible === undefined ? true : action.visible,
  )

  if (visibleActions.length === 0) return null

  const renderButton = (action: ActionButton) => {
    const buttonProps: ButtonProps = {
      id: `btn-${action.key}`,
      disabled: action.disabled,
      variant: action.variant === 'primary' ? 'contained' : 'outlined',
    }

    if ('actions' in action) {
      if (action.type === 'split')
        return (
          <SplitButton
            key={action.key}
            elementId={action.key}
            actions={action.actions}
            label={action.name}
            labelStartIcon={action.labelStartIcon}
            popperPlacement={action.popperPlacement}
            variant={action.variant === 'primary' ? 'contained' : 'outlined'}
            disabled={action.disabled}
          />
        )
      if (action.type === 'dropdown') {
        return (
          <DropdownButton
            key={action.key}
            elementId={action.key}
            label={action.name}
            actions={action.actions}
            variant={action.variant === 'primary' ? 'contained' : 'outlined'}
            popperPlacement={action.popperPlacement}
            disabled={action.disabled}
          />
        )
      }
    }

    if (action.to) {
      // Button acts like a link
      return (
        <Button
          key={action.key}
          component={Link}
          to={action.to}
          type={action.type || 'button'}
          startIcon={action.startIcon}
          {...buttonProps}>
          {action.name}
        </Button>
      )
    }
    // Button acts as JS trigger
    return (
      <Button
        key={action.key}
        onClick={action.onClick}
        type={action.type || 'button'}
        startIcon={action.startIcon}
        {...buttonProps}>
        {action.name}
      </Button>
    )
  }

  const primaryAction = find({ variant: 'primary' }, visibleActions)
  const secondaryAction = find({ variant: 'secondary' }, visibleActions)

  // Compute dynamic right spacing on mobile to avoid overlapping with FAB if present
  const fabElement = typeof window !== 'undefined' ? document.getElementById(FAB_ID) : null
  const hasFab = Boolean(fabElement)
  const mobileContainerWithFabStyle = hasFab
    ? { ...mobileContainerStyle, right: 80 }
    : mobileContainerStyle

  return isTablet ? (
    <Stack
      sx={{
        ...mobileContainerWithFabStyle,
      }}>
      {primaryAction && renderButton(primaryAction)}
    </Stack>
  ) : (
    <Stack spacing={2} flexShrink={0} justifyContent="flex-end" direction="row">
      {!!secondaryAction && renderButton(secondaryAction)}
      {!!primaryAction && renderButton(primaryAction)}
    </Stack>
  )
}

export default ActionButtons
